import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { RackTransferService } from '@shared/services/export-import-services/rack';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { IRack, IResponseRack, RackingService, WorkCenterFilterMode } from '@shared/services/racking.service';
import { SessionStorageService } from '@shared/services/storage.service';
import { catchError, finalize, map, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { RackingCreateUpdateRackDialogComponent } from './racking-create-update/racking-create-update-rack';

@Component({
	selector: 'app-racking-racks',
	templateUrl: './racking.racks.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RackingRacksComponent implements OnInit {
	public isLoading: boolean = true;
	public showSearch: boolean = false;
	public tableSource = new MatTableDataSource<IRack>();
	public racksFilter: string = '';
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public selected: object[] = [];
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	private isInitialLoad: boolean = true;

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.racking.name', dataKey: 'name' },
		{ name: 'masterdata.racking.description', dataKey: 'description' },
		{ name: 'masterdata.racking.cells', dataKey: 'cells' },
		{ name: 'masterdata.racking.rows', dataKey: 'rows' },
		{ name: 'masterdata.racking.columns', dataKey: 'columns' },
		{
			name: 'masterdata.racking.components.label',
			dataKey: 'translationComponents',
		},
		{ name: 'masterdata.racking.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.racking.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.racking.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.racking.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.racking.newbtn.racks',
			click: () => this.openCreateUpdateRack(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.racking.rackImportExport.export.button',
			click: () => this.exportRacks(),
			permissions: () => ['racking:RackList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.racking.rackImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['racking:RackPost', 'racking:RackPut', 'racking:RackList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.racking.rackImportExport.import.template.button',
			click: () => this.rackTransferService.downloadTemplate(),
			permissions: () => ['racking:RackList'],
		},
	];

	public constructor(
		private dialog: MatDialog,
		private translator: TranslateService,
		private rackingService: RackingService,
		private sessionStorageService: SessionStorageService,
		private notificationService: NotificationService,
		private rackTransferService: RackTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateRack(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteRack(data['data']);
				break;
			default:
				break;
		}
	}

	public onDeleteRack(rack: IResponseRack) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.racking.delete.racks.heading'),
				content: this.translator.instant('masterdata.racking.delete.racks.content', {
					name: rack.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.rackingService
						.delete(rack.self)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.racking.delete.racks.notification.header'], ['masterdata.racking.delete.racks.notification.content', { name: rack.name }], 'success');
							this.loadData();
						});
				}
			});
	}

	public handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
	}

	public openCreateUpdateRack(rackData: object = null) {
		const dialogRef = this.dialog.open(RackingCreateUpdateRackDialogComponent, {
			disableClose: true,
			width: '650px',
			data: rackData,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	public exportRacks(): void {
		this.rackingService
			.getRacks()
			.pipe(
				switchMap((racks) => this.rackTransferService.exportData(racks.items)),
				takeUntil(this.unsubscribe),
			)
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.racking.rackImportExport.export.notification.success.header'], ['masterdata.racking.rackImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export racks', error);
					this.notificationService.notify(['masterdata.racking.rackImportExport.export.notification.error.header'], ['masterdata.racking.rackImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.racking.rackImportExport.import.title',
				subHeadingKey: 'masterdata.racking.rackImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.racking.rackImportExport.import.button',
				notificationHeader: 'masterdata.racking.rackImportExport.import.notification.success.header',
				notificationContent: 'masterdata.racking.rackImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.racking.rackImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.racking.rackImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.racking.rackImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.racking.rackImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.rackTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	public ngOnInit(): void {
		this.isLoading = true;
		this.loadData();
		this.listenForLanguageChangeAndLoadData();
	}

	public listenForLanguageChangeAndLoadData() {
		this.sessionStorageService.watch('User.PreferredLanguage').subscribe(() => {
			if (this.isInitialLoad) {
				this.isInitialLoad = false;
			} else {
				if (this.tableSource) {
					this.loadData();
				}
			}
		});
	}

	private loadData(): void {
		this.rackingService
			.getRacks()
			.pipe(
				takeUntil(this.unsubscribe),
				tap((data) => {
					this.isLoading = true;
					this.countSubject$.next(data.items.length);
				}),
				map((data) => {
					const rack$ = data.items.map((rack) => {
						return {
							self: rack.self,
							name: rack.name,
							description: rack.description,
							updatedDate: aciesDateFormat(new Date(rack.meta.updatedAt), true, false),
							updatedAtEpoch: rack.meta.updatedAt,
							updatedBy: this.rackingService.getLastSegment(rack.meta.updatedBy.self),
							cells: rack.configuration.cells,
							rows: rack.configuration.rows,
							columns: rack.configuration.columns,
							components: rack.components.map((component) => {
								const parts: string[] = component.type.self.split('/');
								return parts[parts.length - 1];
							}),
							translationComponents: rack.components
								.map((component) => {
									const parts: string[] = component.type.self.split('/');
									const lastPart: string = parts[parts.length - 1];
									const translation: string = `masterdata.racking.components.${lastPart}`;
									return this.translator.instant(translation);
								})
								.join(', '),
							width: rack.configuration.cell.width,
							height: rack.configuration.cell.height,
							depth: rack.configuration.cell.depth,
							capacity: rack.capacity.maximum,
							workCenterFilterMode: rack.workCenters?.mode ?? WorkCenterFilterMode.Ignore,
							workCenterFilterKeys: rack.workCenters?.keys ?? [],
						};
					});
					return rack$;
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((racks) => {
				this.tableSource.data = racks;
			});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	public handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}
}
